import React, { useRef, useEffect, useMemo, useState } from "react";
import { Grid, Stack } from "@mui/material";
import { useHistory } from "react-router-dom";
import { map, size } from "lodash";
import MemberOverviewTabs from "@icoach/members/overview/MemberOverviewTabsHeader";
import { initBookmark } from "@icoach/members/staticData";
import {
	FilterBirthdaySearch,
	FilterHundredTShirtSearch,
	FilterInvalidSearch,
	FilterLeaveSearch,
	FilterWorkOutTimesSearch,
	FilterHealthCareOrderSearch,
	FilterNewMemberSearch,
	FilterExtensionSearch,
	FilterHighRiskSearch,
	FilterCrossClubSearch,
} from "@icoach/members/overview/searchIndex";
import useMembers from "@icoach/members/components/useMembers";
import { refIsRequiredError, objectToQueryString, paramsToObject } from "@util/utils";
import { isNumber, isEqual, isEmpty, isString } from "@util/utils";
import { SelectField, MenuItem, Card, Button, Form } from "@curves/ui-base";
import useMemberFilterApi from "@apis/useMemberFilterApi";
import useMemberFilterExportApi from "@apis/useMemberFilterExportApi";
import useExportActionDialog from "@util/hook/useExportActionDialog";
import { CLUB_MEMBERS_ROUTER } from "@icoach/router/routerPath";

const quickEnums = [
	{ value: 1, text: "次數名單" },
	{ value: 2, text: "應續約名單" },
	{ value: 3, text: "請假名單" },
	{ value: 4, text: "無效名單" },
	{ value: 5, text: "生日名單" },
	{ value: 6, text: "百T名單" },
	{ value: 7, text: "新會員名單" },
	{ value: 8, text: "健康產品名單" },
	{ value: 9, text: "續約CDE名單" },
	{ value: 10, text: "本店跨店名單" },
	{ value: 16, text: "他店來店名單" },
];

const numberParamKeys = ["pageIndex", "pageSize", "sortBy"];
const crossClubVisitDefaultSort = 3;
const crossClubMemberNoSortValues = [1, 2];

const OverviewFilterSearchBar = props => {
	const { tabsIndex, targetType: quickType } = props;
	const history = useHistory();
	const {
		bookmark,
		sourceData,
		setSourceData,
		resource = {},
		setResource,
		setSortOption,
		setBookmark,
		setBookmarkNum,
		setBookmarkSortBy,
		reSetCheckedItems,
		setRefresh,
		doManualScrollTo,
	} = useMembers();
	const {
		workoutFrequencyOrderByOption: workoutFrequencyOrderOptions = [],
		renewOrderByOption: extensionSortOption = [],
		suspensionOrderByOption: leaveSortOption = [],
		invalidOrderByOption: invalidSortOption = [],
		birthdayOrderByOption: birthdaySortOption = [],
		hundredTShirtOrderByOption: hundredTShirtSortOption = [],
		newOrderByOption: newMemberSortOption = [],
		healthcareOrderByOption: healthcareOrderOptions = [],
		highRiskOrderByOption: highRiskSortOption = [],
		crossClubOrderByOption: crossClubSortOption = [],
		defaultWorkoutFrequencyOrderBy: defaultWorkoutFrequencySort = 1,
		defaultRenewOrderBy: defaultExtensionSort = 1,
		defaultSuspensionOrderBy: defaultLeaveSort = 1,
		defaultInvalidOrderBy: defaultInvalidSort = 1,
		defaultBirthdayOrderBy: defaultBirthdaySort = 1,
		defaultHundredTShirtOrderBy: defaultHundredTShirtSort = 1,
		defaultNewOrderBy: defaultNewMemberSort = 1,
		defaultHealthcareOrderBy: defaultHealthcareSort = 1,
		defaultHighRiskOrderBy: defaultHighRiskSort = 1,
		defaultCrossClubOrderBy: defaultCrossClubSort = 1,
	} = resource;
	const {
		getMemberFilterOptionsApi,
		getWorkOutFrequencyDataApi,
		getMemberBirthDayDataApi,
		getRenewFilterDataApi,
		getLeaveFilterDataApi,
		getInvalidFilterDataApi,
		getHundredTShirtDataApi,
		getNewMemberFilterDataApi,
		getHealthcareFilterDataApi,
		getHighRiskFilterDataApi,
		getCrossClubFilterDataApi,
		getCrossClubVisitFilterDataApi,
	} = useMemberFilterApi();
	const {
		postMemberFilterWorkoutFrequencyApi,
		postMemberFilterHundredTShirtApi,
		postMemberFilterNewApi,
		postMemberFilterInvalidApi,
		postMemberFilterBirthdayApi,
		postMemberFilterLeaveApi,
		postMemberFilterRenewApi,
		postMemberFilterHealthcareApi,
		postMemberFilterHighRiskApi,
		postMemberFilterCrossClubApi,
		postMemberFilterCrossClubVisitApi,
	} = useMemberFilterExportApi();
	const [paramsData, setParamsData] = useState({}); // 初始預設搜尋條件使用,其他狀況下無用
	const [sortStep, setSortStep] = useState("");
	const [isMounted, setMounted] = useState(false);
	const memberFilterTypeRef = useRef(null);
	const searchPanelsRef = useRef(null);
	const preloadRef = useRef(false);
	const { showExportConfirmDialog } = useExportActionDialog();

	const hasSearchError = () => refIsRequiredError(memberFilterTypeRef, searchPanelsRef);

	const parseQueryStringToParams = () => {
		const { location } = history;
		return paramsToObject(location.search, numberParamKeys);
	};

	const getParams = (targetPage = {}) => {
		let paramsJson = { ...targetPage };
		if (searchPanelsRef.current && searchPanelsRef.current.getResult) {
			const target = searchPanelsRef.current.getResult() || {};
			Object.assign(paramsJson, target);
		}

		return paramsJson;
	};

	const handleSearchOnClick = e => {
		if (hasSearchError()) return;
		reSetCheckedItems();
		const params = getParams(initBookmark(bookmark?.sortBy));
		setBookmarkNum(1);
		doSearchApi(params);
		preloadRef.current = true; // 防止切換名單後無法更多與排序
	};

	// 下載excel 寄信給店舖
	const handleExcelOnClick = async () => {
		if (hasSearchError()) return;
		await showExportConfirmDialog(getParams(initBookmark(bookmark?.sortBy)), params => doExcelSendEmailApi(params));
		preloadRef.current = true; // 防止切換名單後無法更多與排序
	};

	// 切換類型
	const handleSelectOnChange = (e, dom, value) => {
		history.push(CLUB_MEMBERS_ROUTER.MEMBER_OVERVIEW.GET_QUICK_PATH(value));
		preloadRef.current = false;
		setSourceData({ list: [] });
	};

	const memberFilterMap = useMemo(
		() => {
			const crossClubVisitSortOption = crossClubSortOption.filter(item => !crossClubMemberNoSortValues.includes(Number(item.value)));
			return {
				1: {
					description: "次數名單",
					sortOption: workoutFrequencyOrderOptions,
					defaultSort: defaultWorkoutFrequencySort,
					Component: FilterWorkOutTimesSearch,
					excelApi: postMemberFilterWorkoutFrequencyApi,
					doApi: getWorkOutFrequencyDataApi,
				},
				2: {
					description: "應續約名單",
					sortOption: extensionSortOption,
					defaultSort: defaultExtensionSort,
					Component: FilterExtensionSearch,
					excelApi: postMemberFilterRenewApi,
					doApi: getRenewFilterDataApi,
				},
				3: {
					description: "請假名單",
					sortOption: leaveSortOption,
					defaultSort: defaultLeaveSort,
					Component: FilterLeaveSearch,
					excelApi: postMemberFilterLeaveApi,
					doApi: getLeaveFilterDataApi,
				},
				4: {
					description: "無效名單",
					sortOption: invalidSortOption,
					defaultSort: defaultInvalidSort,
					Component: FilterInvalidSearch,
					excelApi: postMemberFilterInvalidApi,
					doApi: getInvalidFilterDataApi,
				},
				5: {
					description: "生日名單",
					sortOption: birthdaySortOption,
					defaultSort: defaultBirthdaySort,
					Component: FilterBirthdaySearch,
					excelApi: postMemberFilterBirthdayApi,
					doApi: getMemberBirthDayDataApi,
				},
				6: {
					description: "百T名單",
					sortOption: hundredTShirtSortOption,
					defaultSort: defaultHundredTShirtSort,
					Component: FilterHundredTShirtSearch,
					excelApi: postMemberFilterHundredTShirtApi,
					doApi: getHundredTShirtDataApi,
				},
				7: {
					description: "新會員名單",
					sortOption: newMemberSortOption,
					defaultSort: defaultNewMemberSort,
					Component: FilterNewMemberSearch,
					excelApi: postMemberFilterNewApi,
					doApi: getNewMemberFilterDataApi,
				},
				8: {
					description: "健康產品名單",
					sortOption: healthcareOrderOptions,
					defaultSort: defaultHealthcareSort,
					Component: FilterHealthCareOrderSearch,
					excelApi: postMemberFilterHealthcareApi,
					doApi: getHealthcareFilterDataApi,
				},
				9: {
					description: "高風險名單",
					sortOption: highRiskSortOption,
					defaultSort: defaultHighRiskSort,
					Component: FilterHighRiskSearch,
					excelApi: postMemberFilterHighRiskApi,
					doApi: getHighRiskFilterDataApi,
				},
				10: {
					description: "本店跨店名單",
					sortOption: crossClubSortOption,
					defaultSort: defaultCrossClubSort,
					Component: FilterCrossClubSearch,
					excelApi: postMemberFilterCrossClubApi,
					doApi: getCrossClubFilterDataApi,
				},
				16: {
					description: "他店來店名單",
					sortOption: crossClubVisitSortOption,
					defaultSort: crossClubVisitDefaultSort,
					Component: FilterCrossClubSearch,
					excelApi: postMemberFilterCrossClubVisitApi,
					doApi: getCrossClubVisitFilterDataApi,
				},
			};
		},
		// eslint-disable-next-line
		[resource]
	);

	// 他店來店名單不顯示會員編號，若 URL 或舊狀態殘留會員編號排序，送出前改回來店名單預設排序。
	const normalizeQuickParams = params => {
		const targetParams = { ...params };
		if (isEqual(String(quickType), "16") && crossClubMemberNoSortValues.includes(Number(targetParams.sortBy))) {
			targetParams.sortBy = crossClubVisitDefaultSort;
		}
		return targetParams;
	};

	// 快速名單初始排序options、重刷頁面進來
	const doInitSortOptionsApi = async () => {
		const resp = await getMemberFilterOptionsApi();
		if (resp) {
			setResource(resp);
		}
	};

	// 快速名單總覽
	const doSearchApi = async params => {
		const targetParams = normalizeQuickParams(params);
		const doApi = memberFilterMap[quickType]["doApi"];
		const resp = await doApi(targetParams);
		if (resp) {
			let { list, sortBy, ...pageParams } = resp;
			// 簡訊使用name當作key
			list = map(list, par => ({ name: par.memberName, ...par }));
			setSourceData(prev => {
				let targetList = [...list];
				if (String(targetParams.pageIndex) !== "1") {
					targetList = [...(Array.isArray(prev.list) ? prev.list : []), ...targetList];
				}
				return {
					...pageParams,
					list: targetList,
				};
			});
			const router = CLUB_MEMBERS_ROUTER.MEMBER_OVERVIEW.GET_QUICK_PATH(quickType);
			const url = `${router}${objectToQueryString(targetParams)}`;
			history.replace(url);

			preloadRef.current = true;
		}
		if (!isEmpty(paramsData)) {
			setParamsData({}); // 防止換回同類型初始參數
		}
	};

	const doExcelSendEmailApi = async params => {
		const doApi = memberFilterMap[quickType]["excelApi"];
		// 匯出結果訊息由 API 層（useAjax/useSnackbar）統一處理。
		await doApi(normalizeQuickParams(params));
	};

	// STEP1. 初始排序options
	useEffect(() => {
		if (isEmpty(resource)) {
			preloadRef.current = false;
			doInitSortOptionsApi();
		}

		// eslint-disable-next-line
	}, []);

	//STEP2-1. 變更篩選名單
	useEffect(
		() => {
			if (isNumber(quickType) && !isEmpty(resource)) {
				reSetCheckedItems();

				const { sortOption = [], defaultSort = 1 } = memberFilterMap[quickType];
				setSortOption(sortOption);
				setBookmarkSortBy(defaultSort);
			}
		},
		// eslint-disable-next-line
		[quickType]
	);

	// STEP2-2. 換頁
	useEffect(
		() => {
			if (preloadRef.current) {
				const params = getParams(bookmark);
				if (bookmark && !isEqual(bookmark.pageIndex, 1)) {
					const router = CLUB_MEMBERS_ROUTER.MEMBER_OVERVIEW.GET_QUICK_PATH(quickType);
					const url = `${router}${objectToQueryString(params)}`;
					history.replace(url);
					doSearchApi(params);
				}
			}
		},
		// eslint-disable-next-line
		[bookmark.pageIndex]
	);

	// STEP2-3. 變更排序
	useEffect(
		() => {
			//page2 change sort
			if (preloadRef.current && sourceData && !isEmpty(sourceData.list)) {
				const params = getParams(bookmark);
				const router = CLUB_MEMBERS_ROUTER.MEMBER_OVERVIEW.GET_QUICK_PATH(quickType);
				const url = `${router}${objectToQueryString(params)}`;
				history.replace(url);
				doSearchApi(params);
			} else if (isEqual(sortStep, "START")) {
				setSortStep("COMPLETE");
			}
		},
		// eslint-disable-next-line
		[bookmark.sortBy, sortStep]
	);

	// STEP3. 快速名單-初始設定值、讀取QueryString參數, 預設後回STEP2
	useEffect(
		() => {
			if (isNumber(quickType) && !isEmpty(resource)) {
				reSetCheckedItems();

				const { sortOption = [], defaultSort = 1 } = memberFilterMap[quickType];
				setSortOption(sortOption);

				// 重新載入頁面才會觸發，當下的頁面切換quickType不會進來
				const params = normalizeQuickParams(parseQueryStringToParams());
				if (!isEmpty(params)) {
					setParamsData(params);
					setBookmark({ pageIndex: params.pageIndex, sortBy: params.sortBy });
				} else {
					setBookmarkSortBy(defaultSort);
				}
				setSortStep("START");
			}
		},
		// eslint-disable-next-line
		[memberFilterMap]
	);

	// STEP4. 若有舊清單資訊則讀取，無則停止
	useEffect(
		() => {
			if (isEqual(sortStep, "COMPLETE")) {
				preloadRef.current = false;

				let params = normalizeQuickParams(parseQueryStringToParams());
				if (!isEmpty(params) && size(sourceData) === 0) {
					if (isEqual(quickType, 8)) {
						const { saleCategory } = params;
						if (!isEmpty(saleCategory) && isString(saleCategory)) {
							params = {
								...params,
								saleCategory: saleCategory.split(","),
							};
						}
					}
					doSearchApi({ ...params, ...initBookmark(params.sortBy) });
				} else {
					doManualScrollTo();
					preloadRef.current = true;
				}

				setMounted(true);
			}
		},
		// eslint-disable-next-line
		[sortStep]
	);

	setRefresh(() => {
		const params = getParams(bookmark);
		doSearchApi(params);
	});

	const CompPanel = quickType ? memberFilterMap[quickType]["Component"] : null;

	return (
		<Form onSubmit={handleSearchOnClick}>
			<Card>
				<Card.CardContent className="members-tabs tabs-bottom-line">
					<MemberOverviewTabs value={tabsIndex} />
				</Card.CardContent>
				<Card.CardContent className="pb-4 px-2">
					<Grid container spacing={2}>
						<Grid item xs={2}>
							<SelectField label="篩選名單" value={quickType} onChange={handleSelectOnChange} ref={memberFilterTypeRef} fullWidth>
								{quickEnums.map(({ value, text }) => {
									return (
										<MenuItem key={value} value={value}>
											{text}
										</MenuItem>
									);
								})}
							</SelectField>
						</Grid>
						{isMounted && CompPanel ? <CompPanel key={quickType} ref={searchPanelsRef} resource={resource} paramsData={paramsData} /> : null}
						<Grid xs={3} item>
							<Stack direction="row" spacing={1} className="pt-4">
								<Button variant="contained" type="submit">
									搜尋
								</Button>
								<Button variant="contained" color="success" onClick={handleExcelOnClick}>
									下載Excel
								</Button>
							</Stack>
						</Grid>
					</Grid>
				</Card.CardContent>
			</Card>
		</Form>
	);
};

export default OverviewFilterSearchBar;
